import {
  BackupActions,
  BackupActionsInterface,
  DocumentsForUploadRowData,
  DocumentsForUploadRowType,
  OrderInEditStatusInterface,
  OrdersCacheEntry
} from '../../db-description';
import { Status } from '../../dto/status.enum';
import { Order } from '../../dto/order.dto';

interface SyncActionsTableEntry {
  user: string;
  syncActionsType?: string;
  actionTargetValue?: string;
  syncActions: any[];
}

interface TableConfig<T> {
  testRecord: T;
  keyPath: string | string[];
  getKey: (record: T) => any;
}

interface DBTestConfig {
  [key: string]: TableConfig<any>;
  orders: TableConfig<OrdersCacheEntry>;
  ordersInWork: TableConfig<OrdersCacheEntry>;
  ordersCompleted: TableConfig<OrdersCacheEntry>;
  ordersInEditStatus: TableConfig<OrderInEditStatusInterface>;
  BackupActionsTable: TableConfig<BackupActionsInterface>;
  SyncActionsTable: TableConfig<SyncActionsTableEntry>;
  DocumentsForUpload: TableConfig<DocumentsForUploadRowData>;
}

export const DB_TEST_CONFIG: DBTestConfig = {
  orders: {
    testRecord: {
      pageId: 0,
      filter: 'test_filter',
      user: 'test_user',
      response: {
        data: {
          requests: [],
          total: 0,
          currentPageSize: 0
        },
        success: true
      }
    },
    keyPath: ['pageId', 'filter', 'user'],
    getKey: (record: OrdersCacheEntry) => [
      record.pageId,
      record.filter,
      record.user
    ]
  },
  ordersInWork: {
    testRecord: {
      pageId: 0,
      filter: 'test_filter',
      user: 'test_user',
      response: {
        data: {
          requests: [],
          total: 0,
          currentPageSize: 0
        },
        success: true
      }
    },
    keyPath: ['pageId', 'filter', 'user'],
    getKey: (record: OrdersCacheEntry) => [
      record.pageId,
      record.filter,
      record.user
    ]
  },
  ordersCompleted: {
    testRecord: {
      pageId: 0,
      filter: 'test_filter',
      user: 'test_user',
      response: {
        data: {
          requests: [],
          total: 0,
          currentPageSize: 0
        },
        success: true
      }
    },
    keyPath: ['pageId', 'filter', 'user'],
    getKey: (record: OrdersCacheEntry) => [
      record.pageId,
      record.filter,
      record.user
    ]
  },
  ordersInEditStatus: {
    testRecord: {
      Id: -999999,
      Order: new Order({
        id: -999999,
        status: Status.IN_WORK,
        buyer: {
          address: '',
          id: '',
          inn: '',
          kpp: '',
          orgName: '',
          phone: ''
        },
        createdDate: Date.now(),
        cropYear: 2023,
        consignee: { inn: '', kpp: '', name: '', ogrn: '', address: '' },
        transporterOrderId: '-999999',
        declarationOfConformityDate: Date.now(),
        declarationOfConformityNumber: '',
        distance: 0,
        forwarder: {
          address: '',
          id: -999999,
          inn: '',
          orgName: '',
          phone: '',
          kpp: '',
          okpo: '',
          bankDetails: ''
        },
        forwarderFee: 0,
        shipmentDates: [],
        freightAttributes: [],
        freightName: '',
        bargainId: -999999,
        loading: {
          regionName: '',
          address: '',
          districtId: -999999,
          regionId: -999999,
          hasScales: false,
          hasTrawls: false,
          heightLimits: 0,
          loadingMethod: '',
          maxTonPerDay: 0,
          scaleLength: 0,
          title: '',
          workFrom: '',
          workTo: ''
        },
        quarantineCertificate: false,
        deliveredWeightInKilograms: 0,
        registry: { completed: false, days: [] },
        requestedWeightInTons: 0,
        sampling: false,
        seller: { id: -999999, inn: '', orgName: '', okpo: '', phone: '' },
        unloading: {
          regionName: '',
          address: '',
          districtId: -999999,
          regionId: -999999,
          fiasId: '',
          title: ''
        },
        updatedDate: Date.now(),
        version: 1,
        contractName: '',
        log: [],
        unloadedDeliveryInfo: { unloadedWeight: 0, unloadedDays: [] }
      }),
      orderBackupVersion: 1,
      user: 'test_user'
    },
    keyPath: 'Id',
    getKey: (record: OrderInEditStatusInterface) => record.Id
  },
  BackupActionsTable: {
    testRecord: {
      user: 'test_user',
      Id: -999999,
      orderBackupVersion: 1,
      backupAction: BackupActions.SYNC_FROM_CLIENT,
      idempotencyKey: 'test_key'
    },
    keyPath: ['user', 'Id', 'orderBackupVersion'],
    getKey: (record: BackupActionsInterface) => [
      record.user,
      record.Id,
      record.orderBackupVersion
    ]
  },
  SyncActionsTable: {
    testRecord: {
      user: 'test_user_syncactions',
      syncActionsType: 'test_type',
      actionTargetValue: 'test_value',
      syncActions: []
    },
    keyPath: 'user',
    getKey: (record: SyncActionsTableEntry) => record.user
  },
  DocumentsForUpload: {
    testRecord: {
      user: 'test_user',
      forwardingRequestId: -999999,
      docType: DocumentsForUploadRowType.FORWARDER_REPORT,
      orderNumber: 'test_order',
      vehicleDataId: '-999999',
      updatedOn: Date.now(),
      data: {}
    },
    keyPath: [
      'user',
      'forwardingRequestId',
      'docType',
      'orderNumber',
      'vehicleDataId'
    ],
    getKey: (record: DocumentsForUploadRowData) => [
      record.user,
      record.forwardingRequestId,
      record.docType,
      record.orderNumber,
      record.vehicleDataId
    ]
  }
};

// Экспортируем интерфейсы для использования в других файлах
export { SyncActionsTableEntry, TableConfig, DBTestConfig };
